'use client';

import { useTranslations } from 'next-intl';

import type { QueueRunnerState } from '../lib/api/daemon';
import { badgeTone } from '../lib/badgeColors';
import { MetaBadge } from './MetaBadge';

type DaemonTriggerQueuePositionBadgeProps = {
  status: string;
  // 서버가 내려주는 소유 러너 대기열 순번. 종료 상태·공유 러너·미지정 요청은 null.
  // 클라이언트에서 재계산하지 않는다(runnerQueue.buildQueuePositionMap이 유일 계산 소스).
  queuePosition: number | null;
  // 순번과 같은 계산 경로에서 온 대상 러너 상태. 순번만 보여주면 "곧 실행된다"는 기대를 주므로
  // 오프라인은 톤으로, 나머지는 툴팁 문구로 구분한다.
  queueRunnerState?: QueueRunnerState | null;
  size?: 'sm' | 'xs';
};

const tooltipKeyByRunnerState: Record<QueueRunnerState, string> = {
  IDLE: 'queueWaitingIdle',
  RUNNING: 'queueWaitingRunning',
  OFFLINE: 'queueWaitingOffline',
};

/**
 * "이 요청은 소유 러너 대기열에서 몇 번째다"를 알려주는 순번 배지.
 *
 * 러너 요청 목록·홈 최근 러너 위젯·프로젝트 대시보드가 같은 필드·문구·톤을 쓰도록 이 컴포넌트 한 곳에 둔다.
 * PENDING이 아니거나 순번을 모르면(null) 아무것도 렌더하지 않는다.
 */
export const DaemonTriggerQueuePositionBadge = ({
  status,
  queuePosition,
  queueRunnerState,
  size = 'sm',
}: DaemonTriggerQueuePositionBadgeProps) => {
  const t = useTranslations('common');
  if (status !== 'PENDING' || queuePosition == null) {
    return null;
  }

  // 오프라인 러너의 대기는 무기한이라 "진행 중"과 같은 톤으로 보이면 안 된다.
  const tone = queueRunnerState === 'OFFLINE' ? badgeTone.neutral : badgeTone.primary;
  const tooltip = queueRunnerState
    ? t(tooltipKeyByRunnerState[queueRunnerState], { position: queuePosition })
    : undefined;

  return (
    <MetaBadge tone={tone} size={size} className="shrink-0" tooltip={tooltip}>
      {t('queueWaiting', { position: queuePosition })}
    </MetaBadge>
  );
};
